import { useState, useEffect } from 'react';
import {
  DEFAULT_PROFILE_DATA,
  DEFAULT_NOTIFICATION_SETTINGS,
  DEFAULT_PASSWORD_DATA,
  DEFAULT_SHOW_PASSWORDS,
} from '../../../constants/settingsPage';

/**
 * Hook to manage page state for Settings page
 * @param {Object} user - Current user object from AuthContext
 * @returns {Object} Page state and handlers
 */
export const useSettingsPageState = (user) => {
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState(DEFAULT_PROFILE_DATA);
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_SETTINGS);
  const [passwordData, setPasswordData] = useState(DEFAULT_PASSWORD_DATA);
  const [showPasswords, setShowPasswords] = useState(DEFAULT_SHOW_PASSWORDS);
  const [isSavingEmailNotification, setIsSavingEmailNotification] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Update profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user?.full_name || user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        whatsappNumber: user?.whatsappNumber || '',
        designation: user?.designation || '',
        department: user?.department || '',
      });
    }
  }, [user]);

  // Initialize notification settings from user
  useEffect(() => {
    if (user?.emailNotifications !== undefined) {
      setNotificationSettings((prev) => ({
        ...prev,
        emailNotifications: user.emailNotifications,
      }));
    }
  }, [user]);

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const resetProfileData = () => {
    if (user) {
      setProfileData({
        name: user?.full_name || user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        whatsappNumber: user?.whatsappNumber || '',
        designation: user?.designation || '',
        department: user?.department || '',
      });
    }
  };

  const resetPasswordData = () => {
    setPasswordData(DEFAULT_PASSWORD_DATA);
    setShowPasswords(DEFAULT_SHOW_PASSWORDS);
  };

  return {
    // State
    isEditingProfile,
    profileData,
    notificationSettings,
    passwordData,
    showPasswords,
    isSavingEmailNotification,
    isLoadingPreferences,
    isSavingProfile,
    isChangingPassword,
    // Setters
    setIsEditingProfile,
    setProfileData,
    setNotificationSettings,
    setPasswordData,
    setShowPasswords,
    setIsSavingEmailNotification,
    setIsLoadingPreferences,
    setIsSavingProfile,
    setIsChangingPassword,
    // Handlers
    handleProfileChange,
    handlePasswordChange,
    togglePasswordVisibility,
    resetProfileData,
    resetPasswordData,
  };
};
